<script lang="ts" setup>
import { AppButton, AppLogo, AppPassword, AppTextField, AppTypography } from '@/core/components';
import type { CommonErrorResponse } from '@/types/ErrorResponse';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useAuthStore } from '../store/auth';
import type { LoginForm } from '../types';

const config = useRuntimeConfig();
const { VALIDATIONS, PASSWORD_VALIDATION } = useValidation();
const { form, handleSubmit, getErrors } = useForm<LoginForm>({
  form: {
    email: '',
    password: '',
  },
  rules: {
    email: {
      required: VALIDATIONS.required('Email'),
      email: VALIDATIONS.email(),
    },
    password: PASSWORD_VALIDATION,
  },
});

const { user, isVerified, hasCompletedOnboarding } = storeToRefs(useAuthStore());
const router = useRouter();

const {
  pending: verifyLoading,
  execute: execSendVerify,
  error: verifyError,
} = useSubmit<{}, { data: CommonErrorResponse }>();

const { token, initToken } = await useGenerateReCaptcha('login');
const { setToken } = useAuthState();
const { getSession } = useAuth();
const loginError = ref({ data: { message: '' } });

const { data, pending: loginLoading, execute: execSubmit } = useSubmit();
async function handleSignIn(): Promise<void> {
  handleSubmit(async function () {
    try {
      loginLoading.value = true;
      await execSubmit('auth/login', {
        method: 'POST',
        body: {
          username: form.value.email,
          password: form.value.password,
        },
        headers: {
          ...(token.value && { 'X-Recaptcha-Token': token.value }),
        },
      });

      if (data.value) {
        setToken(data.value.data.access_token);
        await getSession();
        user.value = data.value.data.user;
        await nextTick();

        if (!isVerified.value) {
          await execSendVerify('auth/verification/resend', {
            method: 'POST',
            body: { via: 'email' },
          });

          if (!verifyError.value) {
            router.push({ name: 'verify' });
          }
          return;
        }

        router.push({ name: 'index' });
      }
    } catch (error) {
      initToken();
      loginError.value.data = error.data;
    } finally {
      loginLoading.value = false;
    }
  });
}

const errors = computed(() => {
  return getErrors({ email: loginError.value?.data?.message });
});
</script>

<template>
  <v-form @submit.prevent="handleSignIn">
    <ClientOnly>
      <div>
        <div class="d-flex flex-column align-center gap-md padding-md">
          <AppLogo />
          <AppTypography element="h2" type="title-1" class="text-white">{{ config.public.appName }}</AppTypography>
        </div>
        <div class="signin-subtitle">
          <AppTypography type="headline-2" class="text-white text-center"
            >Enter your email and password to sign in</AppTypography
          >
        </div>
      </div>

      <div class="signin-form">
        <AppTextField
          v-model="form.email"
          :error-messages="errors.email"
          label="Email"
          :readonly="loginLoading"
          autocomplete="email"
          data-testid="email"
          autofocus
          data-cy="email"
        >
        </AppTextField>
        <AppPassword v-model="form.password" :error-messages="errors.password" data-cy="password" />
        <nuxt-link to="/reset-password" class="text-decoration-none">
          <AppTypography type="headline-1" class="text-white forgot-password">Forgot Password</AppTypography>
        </nuxt-link>
      </div>

      <AppButton block data-cy="submit" data-testid="submit" :loading="loginLoading || verifyLoading"
        >Continue</AppButton
      >
    </ClientOnly>
  </v-form>
</template>

<style lang="scss" scoped>
.v-form {
  display: flex;
  flex-direction: column;
  gap: 40px;
  .signin-subtitle {
    @media (max-width: 768px) {
      @include auth-content();
    }
  }
  .signin-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
    @media (max-width: 768px) {
      .forgot-password {
        color: rgba(var(--v-theme-text-primary)) !important;
      }
    }
  }
}
</style>
